import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import * as RD from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  BarChart3,
  CornerDownLeft,
  Inbox,
  Mail,
  Plus,
  Scale,
  Search,
  Settings2,
  Ticket,
  User,
  UserX,
} from 'lucide-react';
import { api, type TicketState } from '../lib/api.js';
import { ago } from '../lib/format.js';
import { Kbd, StateBadge, cn } from '../ui/index.js';

interface SearchHit {
  id: string;
  tenant_id: string;
  number: number;
  subject: string;
  state: TicketState;
  request_type: string | null;
  updated_at: string;
  requester_name: string | null;
}

/**
 * Command palette.
 *
 * It does the two things people actually want from ⌘K: jump somewhere, and
 * find a ticket. Ticket search is debounced and runs against the same
 * full-text endpoint as the search page, so there is one search implementation
 * rather than a fast fake one in the palette.
 */
export function CommandPalette({
  open,
  onOpenChange,
  tenant,
  isStaff,
  crossTenant,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tenant: string;
  isStaff: boolean;
  crossTenant: boolean;
}) {
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 180);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const results = useQuery({
    queryKey: ['palette-search', debounced],
    queryFn: () =>
      api.get<{ results: SearchHit[] }>(`/api/v1/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2 && isStaff,
  });

  const go = (to: string) => {
    onOpenChange(false);
    nav(to);
  };

  const navItems = [
    ...(isStaff
      ? [
          { icon: <Inbox />, label: 'Queue', to: '/queue', keywords: 'tickets list open' },
          { icon: <BarChart3 />, label: 'Dashboard', to: '/dashboard', keywords: 'reports sla metrics' },
          { icon: <Mail />, label: 'Intake', to: '/intake', keywords: 'email pipeline ladder simulate' },
          { icon: <Settings2 />, label: 'Configuration', to: '/admin', keywords: 'fields rules sla roles' },
        ]
      : []),
    { icon: <User />, label: 'My requests', to: '/my', keywords: 'mine own' },
    { icon: <Plus />, label: 'New request', to: '/new', keywords: 'create raise submit' },
    ...(crossTenant
      ? [{ icon: <Scale />, label: 'Team comparison', to: '/comparison', keywords: 'firm wide cross tenant' }]
      : []),
  ];

  const filters = isStaff
    ? [
        { icon: <AlertTriangle />, label: 'Overdue tickets', to: '/queue?view=breached' },
        { icon: <UserX />, label: 'Unassigned tickets', to: '/queue?view=unassigned' },
        { icon: <Search />, label: 'Needs routing', to: '/queue?view=needs_routing' },
      ]
    : [];

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="anim-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <RD.Content
          aria-label="Command palette"
          className={cn(
            'anim-content fixed left-1/2 top-[12vh] z-50 w-[min(38rem,calc(100vw-2rem))] -translate-x-1/2',
            'overflow-hidden rounded-xl border border-line bg-surface-raised shadow-lg',
          )}
        >
          <Command shouldFilter loop label="Command palette">
            <div className="flex items-center gap-2.5 border-b border-line px-3.5">
              <Search aria-hidden className="h-4 w-4 shrink-0 text-ink-tertiary" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Search tickets, or jump to a page…"
                className="h-12 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-tertiary"
              />
              <Kbd>Esc</Kbd>
            </div>

            <Command.List className="max-h-[22rem] overflow-y-auto overscroll-contain p-1.5">
              {/* Force-mounted ticket hits do not count as matches to cmdk. */}
              {!results.data?.results.length && (
                <Command.Empty className="px-3 py-8 text-center text-xs text-ink-secondary">
                  {debounced.length >= 2
                    ? results.isFetching
                      ? 'Searching…'
                      : 'Nothing matched.'
                    : 'Type at least two characters to search tickets.'}
                </Command.Empty>
              )}

              {/* Ticket hits are given their own group and are NOT filtered
                  client-side — the server already ranked them, often on a
                  message or a person the row does not show. Without
                  forceMount the palette's fuzzy filter hid every hit whose
                  subject did not happen to spell what was typed. */}
              {results.data && results.data.results.length > 0 && (
                <Group heading={`Tickets · ${results.data.results.length}`} forceMount>
                  {results.data.results.slice(0, 8).map((r) => (
                    <Item
                      key={r.id}
                      value={`ticket-${r.number}-${r.subject}`}
                      onSelect={() => go(`/t/${r.tenant_id}/${r.number}`)}
                      icon={<Ticket />}
                      forceMount
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="nums shrink-0 text-2xs text-ink-tertiary">#{r.number}</span>
                        <span className="min-w-0 flex-1 truncate">{r.subject}</span>
                        {r.requester_name && (
                          <span className="hidden max-w-[9rem] shrink-0 truncate text-2xs text-ink-tertiary sm:inline">
                            {r.requester_name}
                          </span>
                        )}
                        <StateBadge state={r.state} />
                        <span className="shrink-0 text-3xs text-ink-tertiary">
                          {ago(r.updated_at)}
                        </span>
                      </span>
                    </Item>
                  ))}
                </Group>
              )}

              <Group heading="Go to">
                {navItems.map((n) => (
                  <Item
                    key={n.to}
                    value={`nav ${n.label} ${n.keywords}`}
                    onSelect={() => go(n.to)}
                    icon={n.icon}
                  >
                    {n.label}
                  </Item>
                ))}
              </Group>

              {filters.length > 0 && (
                <Group heading="Queue views">
                  {filters.map((f) => (
                    <Item
                      key={f.to}
                      value={`view ${f.label}`}
                      onSelect={() => go(f.to)}
                      icon={f.icon}
                    >
                      {f.label}
                    </Item>
                  ))}
                </Group>
              )}
            </Command.List>

            <div className="flex items-center gap-3 border-t border-line bg-surface-sunken/60 px-3 py-2 text-3xs text-ink-tertiary">
              <span className="flex items-center gap-1">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd> navigate
              </span>
              <span className="flex items-center gap-1">
                <Kbd>
                  <CornerDownLeft className="h-2.5 w-2.5" />
                </Kbd>{' '}
                open
              </span>
              <span className="ml-auto">{tenant}</span>
            </div>
          </Command>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

function Group({
  heading,
  forceMount,
  children,
}: {
  heading: string;
  forceMount?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Command.Group
      heading={heading}
      forceMount={forceMount}
      className={cn(
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2',
        '[&_[cmdk-group-heading]]:text-3xs [&_[cmdk-group-heading]]:font-semibold',
        '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider',
        '[&_[cmdk-group-heading]]:text-ink-tertiary',
      )}
    >
      {children}
    </Command.Group>
  );
}

function Item({
  value,
  onSelect,
  icon,
  forceMount,
  children,
}: {
  value: string;
  onSelect: () => void;
  icon: React.ReactNode;
  forceMount?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Command.Item
      value={value}
      forceMount={forceMount}
      onSelect={onSelect}
      className={cn(
        'flex cursor-default select-none items-center gap-2.5 rounded-md px-2 py-2 text-sm text-ink',
        'data-[selected=true]:bg-brand-tint data-[selected=true]:text-brand',
      )}
    >
      <span aria-hidden className="shrink-0 text-ink-tertiary [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </Command.Item>
  );
}
